/**
 * Word highlighter for a page with readable iframes: highlights in the page
 * when the sentence is there, otherwise in whichever frame holds it.
 */
import type { WordHighlightInfo } from '../core/StreamingPlayer';
import { WordHighlighter } from '../ui/WordHighlighter';
import type { HighlightMode } from '../ui/highlightStyles';
import type { FrameManager } from './FrameManager';

export class FrameAwareHighlighter extends WordHighlighter {
  frames: FrameManager | null = null;
  /** Last sentence handed to the frames (so the page doesn't message them per word) */
  private framesSentence = '';

  highlightWord(info: WordHighlightInfo): boolean {
    const here = super.highlightWord(info);
    if (!this.frames) return here;
    if (here) {
      this.leaveFrames(info.sentence);
      return true;
    }
    this.framesSentence = info.sentence;
    return this.frames.highlightWord(info);
  }

  highlightSentenceOnly(sentence: string): boolean {
    const here = super.highlightSentenceOnly(sentence);
    if (!this.frames) return here;
    if (here) {
      this.leaveFrames(sentence);
      return true;
    }
    this.framesSentence = sentence;
    return this.frames.highlightSentence(sentence);
  }

  clearAll(): void {
    super.clearAll();
    this.framesSentence = '';
    this.frames?.clear();
  }

  setStyle(opts: { mode?: HighlightMode; word?: string; sentence?: string }): void {
    super.setStyle(opts);
    this.frames?.setStyle(opts);
  }

  setAutoScroll(enabled: boolean): void {
    super.setAutoScroll(enabled);
    this.frames?.setAutoScroll(enabled);
  }

  /** The sentence is back in the page: clear a frame's highlight once */
  private leaveFrames(sentence: string): void {
    if (this.framesSentence && this.framesSentence !== sentence) {
      this.framesSentence = '';
      this.frames?.clear();
    }
  }
}
